import { store, AppDispatch } from '../store'
import { addNotification } from '../store/slices/uiSlice'
import { notificationService } from './notifications'

// Shared construction for the three notification channels fired per issue
// event (browser Notification, in-app toast, notification-center bell) so
// each socket handler in useIssueNotifications.ts only builds one canonical
// description instead of three separately-authored payloads.

export type IssueNotificationCategory = 'comment' | 'status_change' | 'assignment' | 'new_issue'

export const getIssueUrl = (projectId: string, issueKey: string, projectName?: string): string => {
  // Try to get project from Redux state
  const { projects } = store.getState().projects
  let projectSlug = projectId

  if (projectName) {
    // Use provided project name
    projectSlug = projectName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
  } else {
    // Try to find project in Redux state
    const project = projects.find(p => p._id === projectId)
    if (project) {
      projectSlug = project.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    } else {
      // Fallback: try to get from current location
      const currentPath = window.location.pathname
      const projectMatch = currentPath.match(/\/projects\/([^/]+)/)
      if (projectMatch) {
        projectSlug = projectMatch[1]
      }
    }
  }

  return `/projects/${projectSlug}/kanban/issues/${issueKey}`
}

interface DispatchIssueNotificationParams {
  dispatch: AppDispatch
  category: IssueNotificationCategory
  idPrefix: string
  issueKey: string
  issueTitle: string
  projectId: string
  projectName?: string
  actionBy: { name: string; avatar?: string }
  bellTitle: string
  bellMessage: string
  toastMessage: string
  notifyBrowser: (issueUrl: string) => void
}

export function dispatchIssueNotification(params: DispatchIssueNotificationParams) {
  const issueUrl = getIssueUrl(params.projectId, params.issueKey, params.projectName)

  // (a) Browser notification
  params.notifyBrowser(issueUrl)

  // (b) In-app toast notification
  params.dispatch(addNotification({
    type: 'info',
    message: params.toastMessage
  }))

  // (c) Notification center (bell)
  const notificationEvent = new CustomEvent('new-notification', {
    detail: {
      id: `${params.idPrefix}-${Date.now()}`,
      type: params.category,
      title: params.bellTitle,
      message: params.bellMessage,
      timestamp: new Date().toISOString(),
      read: false,
      issueKey: params.issueKey,
      issueTitle: params.issueTitle,
      projectId: params.projectId,
      projectName: params.projectName,
      actionBy: params.actionBy
    }
  })
  window.dispatchEvent(notificationEvent)
}

// System-wide notifications (e.g. the daily time-log reminder) have no
// issue/project to attach to - same 3 channels as dispatchIssueNotification,
// just without the issue-specific fields.
export function dispatchSystemNotification(dispatch: AppDispatch, message: string) {
  notificationService.showNotification({
    title: 'VEXA',
    body: message
  })

  dispatch(addNotification({
    type: 'info',
    message
  }))

  const notificationEvent = new CustomEvent('new-notification', {
    detail: {
      id: `system-${Date.now()}`,
      type: 'system',
      title: 'Reminder',
      message,
      timestamp: new Date().toISOString(),
      read: false
    }
  })
  window.dispatchEvent(notificationEvent)
}
